/**
 * Finance Report Preview Modal
 *
 * Renders a server-generated HTML preview via the unified finance report API.
 * Supports: Print, Download PDF, Email — for finance reports
 * (profit_loss, balance_sheet, aged_receivables, aged_payables, etc.)
 *
 * @version 1.0.0
 * @date April 2026
 */

'use client';

import dynamic from 'next/dynamic';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Printer, Download, Loader2, Mail, Send, Settings, Users,
} from 'lucide-react';
import EmailRecipientsInput from '@/app/client-zone/modules/total-connect/components/EmailRecipientsInput';
import EmailContactPickerModal from '@/components/reports/EmailContactPickerModal';
import { useAuth } from '@/hooks/useAuth';
import { useNavigation } from '@/hooks/useNavigation';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import { isCapacitor } from '@/lib/platformUtils';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { DOCUMENT_TYPE_LABELS, type DocumentType, type UnifiedTemplate } from '@/lib/reports';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const text = await response.text();
    if (!text) return fallback;
    try {
      const parsed = JSON.parse(text);
      return parsed?.error || parsed?.message || fallback;
    } catch {
      return text;
    }
  } catch {
    return fallback;
  }
}

interface FinanceReportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: DocumentType;
  reportData?: unknown;
  period?: { from: string; to: string };
  reportOptions?: FinanceReportPreviewOption[];
  initialReportOptionId?: string;
  reportOptionPreferenceKey?: string;
  templatePreferenceNamespace?: string;
  resolveReportContext?: (optionId: string) => Promise<FinanceReportPreviewContext>;
}

export interface FinanceReportPreviewContext {
  documentType: DocumentType;
  reportData?: unknown;
  period?: { from: string; to: string };
}

export interface FinanceReportPreviewOption {
  id: string;
  label: string;
  documentType: DocumentType;
}

interface ReportTemplateOption {
  id: number;
  name: string;
  document_type: DocumentType;
  is_default: boolean;
  is_system?: boolean;
}

type EmailSuggestionSource = 'recent' | 'contact' | 'company' | 'crm' | 'personal';

type EmailSuggestion = {
  email: string;
  name?: string;
  source: EmailSuggestionSource;
  avatar_url?: string;
  job_title?: string;
  contact_type?: string;
  company?: string;
};

type EmailSuggestionCandidate = {
  email?: string | null;
  name?: string | null;
  source?: string | null;
  avatar_url?: string | null;
  photo_url?: string | null;
  job_title?: string | null;
  contact_type?: string | null;
  company?: string | null;
};

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function isEmailSuggestionSource(value: string | null | undefined): value is EmailSuggestionSource {
  return ['recent', 'contact', 'company', 'crm', 'personal'].includes(value || '');
}

function normalizeEmailSuggestion(candidate: EmailSuggestionCandidate): EmailSuggestion | null {
  const email = candidate.email?.trim();
  if (!email) {
    return null;
  }

  return {
    email,
    name: candidate.name?.trim() || undefined,
    source: isEmailSuggestionSource(candidate.source) ? candidate.source : 'recent',
    avatar_url: candidate.avatar_url?.trim() || candidate.photo_url?.trim() || undefined,
    job_title: candidate.job_title?.trim() || undefined,
    contact_type: candidate.contact_type?.trim() || undefined,
    company: candidate.company?.trim() || undefined,
  };
}

function parseStoredEmailSuggestions(raw: string | null): EmailSuggestionCandidate[] {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((value): value is EmailSuggestionCandidate => typeof value === 'object' && value !== null)
      : [];
  } catch {
    return [];
  }
}

const safeAreaTop = 'max(env(safe-area-inset-top, 0px), var(--safe-area-top, 0px))';
const safeAreaBottom = 'max(env(safe-area-inset-bottom, 0px), var(--safe-area-bottom, 0px))';

const UnifiedDesigner = dynamic(
  () => import('@/components/designer/UnifiedDesigner').then((m) => m.UnifiedDesigner),
  {
    ssr: false,
    loading: () => (
      <div className="flex items-center justify-center h-full bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    ),
  },
);

export default function FinanceReportPreviewModal({
  isOpen,
  onClose,
  documentType,
  reportData,
  period,
  reportOptions,
  initialReportOptionId,
  reportOptionPreferenceKey,
  templatePreferenceNamespace,
  resolveReportContext,
}: FinanceReportPreviewModalProps) {
  const { user, isRoot } = useAuth();
  const { userRole } = useNavigation();
  const isCapacitorApp = isCapacitor();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const mergedRoleRaw = userRole || user?.role || '';
  const mergedRoleLower = mergedRoleRaw.toLowerCase();
  const canManageTemplates =
    mergedRoleLower === 'owner' ||
    mergedRoleLower === 'root' ||
    isRoot ||
    mergedRoleRaw === 'Root';

  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [previewLabel, setPreviewLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingReportContext, setLoadingReportContext] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<ReportTemplateOption[]>([]);
  const [catalogTemplates, setCatalogTemplates] = useState<ReportTemplateOption[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedReportOptionId, setSelectedReportOptionId] = useState<string | null>(initialReportOptionId || reportOptions?.[0]?.id || null);
  const [resolvedReportContext, setResolvedReportContext] = useState<FinanceReportPreviewContext | null>(
    reportOptions?.length ? null : { documentType, reportData, period }
  );
  const [reportContextError, setReportContextError] = useState<string | null>(null);
  const [templateDesignerOpen, setTemplateDesignerOpen] = useState(false);
  const [designerTemplateId, setDesignerTemplateId] = useState<number | undefined>(undefined);
  const [openingDesigner, setOpeningDesigner] = useState(false);
  const isDynamicReportMode = Boolean(reportOptions?.length && resolveReportContext);
  const selectedReportOption = useMemo(
    () => reportOptions?.find((option) => option.id === selectedReportOptionId) || reportOptions?.[0] || null,
    [reportOptions, selectedReportOptionId],
  );
  const effectiveDocumentType = selectedReportOption?.documentType || resolvedReportContext?.documentType || documentType;
  const effectiveReportData = isDynamicReportMode ? resolvedReportContext?.reportData : reportData;
  const effectivePeriod = isDynamicReportMode ? resolvedReportContext?.period : period;
  const templatePreferenceKey = templatePreferenceNamespace
    ? `${templatePreferenceNamespace}:${effectiveDocumentType}`
    : `ta_finance_report_template:${effectiveDocumentType}`;

  // Email state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailBcc, setEmailBcc] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState<EmailSuggestion[]>([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [selectedEmailSuggestionIndex, setSelectedEmailSuggestionIndex] = useState(-1);
  const [activeEmailField, setActiveEmailField] = useState<'to' | 'cc' | 'bcc'>('to');
  const emailToInputRef = useRef<HTMLInputElement>(null);
  const emailCcInputRef = useRef<HTMLInputElement>(null);
  const emailBccInputRef = useRef<HTMLInputElement>(null);

  // ── Load HTML preview ─────────────────────────────────────

  const extractEmailAddresses = useCallback((value: string): string[] => {
    if (!value?.trim()) return [];
    const parts: string[] = [];
    let current = '';
    let inBracket = false;
    for (const char of value) {
      if (char === '<') inBracket = true;
      if (char === '>') inBracket = false;
      if (char === ',' && !inBracket) {
        if (current.trim()) parts.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    if (current.trim()) parts.push(current.trim());
    return parts.map((part) => {
      const match = part.match(/<([^<>]+@[^<>]+)>/);
      return match ? match[1].trim() : part.trim();
    }).filter((email) => email.includes('@'));
  }, []);

  const loadEmailSuggestions = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setEmailSuggestions([]);
      setShowEmailSuggestions(false);
      return;
    }

    try {
      const q = query.toLowerCase();
      const recent = parseStoredEmailSuggestions(localStorage.getItem('recentEmailRecipients'));
      const company = parseStoredEmailSuggestions(localStorage.getItem('companyEmailUsers'));
      const cached = [...recent, ...company].filter(
        (suggestion) => suggestion.email?.toLowerCase().includes(q) || suggestion.name?.toLowerCase().includes(q),
      );
      const unique = cached.filter(
        (suggestion, index, arr) => arr.findIndex((item) => item.email?.toLowerCase() === suggestion.email?.toLowerCase()) === index,
      );
      setEmailSuggestions(unique.slice(0, 8).map(normalizeEmailSuggestion).filter((value): value is EmailSuggestion => value !== null));
      setShowEmailSuggestions(unique.length > 0);
      setSelectedEmailSuggestionIndex(-1);

      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'searchAllContacts', query }),
      });

      if (!response.ok) return;
      const data = await response.json() as { success?: boolean; data?: EmailSuggestionCandidate[] };
      const matchedContacts = Array.isArray(data.data) ? data.data : [];
      if (data.success && matchedContacts.length > 0) {
        const all = [...unique, ...matchedContacts];
        const deduped = all.filter(
          (suggestion, index, arr) => arr.findIndex((item) => item.email?.toLowerCase() === suggestion.email?.toLowerCase()) === index,
        );
        setEmailSuggestions(deduped.slice(0, 10).map(normalizeEmailSuggestion).filter((value): value is EmailSuggestion => value !== null));
        setShowEmailSuggestions(deduped.length > 0);
      }
    } catch {
      // cached suggestions are enough if the server search is unavailable
    }
  }, []);

  const selectEmailSuggestion = useCallback((suggestion: { email: string; name?: string }) => {
    const current = activeEmailField === 'cc' ? emailCc : activeEmailField === 'bcc' ? emailBcc : emailTo;
    const setter = activeEmailField === 'cc' ? setEmailCc : activeEmailField === 'bcc' ? setEmailBcc : setEmailTo;
    const parts = current.split(',').map((part) => part.trim());
    parts[parts.length - 1] = suggestion.name ? `${suggestion.name} <${suggestion.email}>` : suggestion.email;
    setter(parts.join(', ') + ', ');
    setShowEmailSuggestions(false);
    setEmailSuggestions([]);
    setSelectedEmailSuggestionIndex(-1);
  }, [activeEmailField, emailBcc, emailCc, emailTo]);

  const handleEmailFieldKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!showEmailSuggestions || emailSuggestions.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedEmailSuggestionIndex((index) => Math.min(index + 1, emailSuggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedEmailSuggestionIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && selectedEmailSuggestionIndex >= 0) {
      event.preventDefault();
      selectEmailSuggestion(emailSuggestions[selectedEmailSuggestionIndex]);
    } else if (event.key === 'Escape') {
      setShowEmailSuggestions(false);
    }
  }, [emailSuggestions, selectedEmailSuggestionIndex, selectEmailSuggestion, showEmailSuggestions]);

  const appendRecipientsToField = useCallback((recipients: Array<{ email: string; name?: string }>, field: 'to' | 'cc' | 'bcc' = 'to') => {
    const currentValue = field === 'cc' ? emailCc : field === 'bcc' ? emailBcc : emailTo;
    const currentEmails = new Set(extractEmailAddresses(currentValue).map((email) => email.toLowerCase()));
    const existingParts = currentValue.split(',').map((part) => part.trim()).filter(Boolean);
    const additions = recipients
      .filter((recipient) => !currentEmails.has(recipient.email.toLowerCase()))
      .map((recipient) => recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email);
    const nextValue = [...existingParts, ...additions].join(', ');
    if (field === 'cc') {
      setEmailCc(nextValue ? `${nextValue}, ` : '');
    } else if (field === 'bcc') {
      setEmailBcc(nextValue ? `${nextValue}, ` : '');
    } else {
      setEmailTo(nextValue ? `${nextValue}, ` : '');
    }
  }, [emailBcc, emailCc, emailTo, extractEmailAddresses]);

  const readStoredTemplatePreference = useCallback((): number | null | undefined => {
    if (typeof window === 'undefined') return undefined;
    const raw = window.localStorage.getItem(templatePreferenceKey);
    if (!raw) return undefined;
    if (raw === 'default') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [templatePreferenceKey]);

  const readStoredReportOptionPreference = useCallback((): string | null | undefined => {
    if (typeof window === 'undefined' || !reportOptionPreferenceKey) {
      return undefined;
    }

    const raw = window.localStorage.getItem(reportOptionPreferenceKey);
    return raw && raw.trim().length > 0 ? raw.trim() : undefined;
  }, [reportOptionPreferenceKey]);

  useEffect(() => {
    if (!reportOptions?.length) {
      setSelectedReportOptionId(null);
      return;
    }

    setSelectedReportOptionId((current) => {
      if (current && reportOptions.some((option) => option.id === current)) {
        return current;
      }

      const storedReportOptionId = readStoredReportOptionPreference();
      if (storedReportOptionId && reportOptions.some((option) => option.id === storedReportOptionId)) {
        return storedReportOptionId;
      }

      if (initialReportOptionId && reportOptions.some((option) => option.id === initialReportOptionId)) {
        return initialReportOptionId;
      }

      return reportOptions[0]?.id || null;
    });
  }, [initialReportOptionId, readStoredReportOptionPreference, reportOptions]);

  useEffect(() => {
    if (typeof window === 'undefined' || !reportOptionPreferenceKey || !selectedReportOptionId) {
      return;
    }

    window.localStorage.setItem(reportOptionPreferenceKey, selectedReportOptionId);
  }, [reportOptionPreferenceKey, selectedReportOptionId]);

  useEffect(() => {
    if (isDynamicReportMode) {
      return;
    }

    setReportContextError(null);
    setResolvedReportContext({ documentType, reportData, period });
  }, [documentType, isDynamicReportMode, period, reportData]);

  useEffect(() => {
    if (!isOpen || !isDynamicReportMode || !resolveReportContext || !selectedReportOptionId) {
      return;
    }

    let cancelled = false;
    setLoadingReportContext(true);
    setReportContextError(null);
    setSelectedTemplateId(null);
    setResolvedReportContext(null);

    void (async () => {
      try {
        const nextContext = await resolveReportContext(selectedReportOptionId);

        if (cancelled) {
          return;
        }

        setResolvedReportContext({
          documentType: nextContext.documentType || selectedReportOption?.documentType || documentType,
          reportData: nextContext.reportData,
          period: nextContext.period,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Failed to resolve finance report context:', error);
        setReportContextError(error instanceof Error ? error.message : 'Failed to load finance report data');
      } finally {
        if (!cancelled) {
          setLoadingReportContext(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [documentType, isDynamicReportMode, isOpen, resolveReportContext, selectedReportOption?.documentType, selectedReportOptionId]);

  const loadTemplates = useCallback(async (preferredTemplateId?: number | null, documentTypeOverride?: DocumentType) => {
    if (!isOpen) return;
    setLoadingTemplates(true);
    try {
      const token = authService.getTokens()?.access_token;
      const currentDocumentType = documentTypeOverride || effectiveDocumentType;
      const res = await fetch(`/api/v1/reports/templates?documentType=${currentDocumentType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load templates');

      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load templates');

      const data = json.data || {};
      const nextTemplates: ReportTemplateOption[] = Array.isArray(data.templates) ? data.templates : [];
      const nextCatalog: ReportTemplateOption[] = Array.isArray(data.catalog) ? data.catalog : [];
      const storedPreference = readStoredTemplatePreference();

      setAvailableTemplates(nextTemplates);
      setCatalogTemplates(nextCatalog);

      setSelectedTemplateId((current) => {
        const requested = preferredTemplateId !== undefined
          ? preferredTemplateId
          : storedPreference !== undefined
            ? storedPreference
            : current;
        if (requested && nextTemplates.some((template) => template.id === requested)) {
          return requested;
        }
        if (requested === null) {
          return null;
        }
        if (storedPreference && nextTemplates.some((template) => template.id === storedPreference)) {
          return storedPreference;
        }
        return nextTemplates.find((template) => template.is_default)?.id ?? nextTemplates[0]?.id ?? null;
      });
    } catch (err) {
      console.error('[FinanceReportPreviewModal] template load error:', err);
      setAvailableTemplates([]);
      setCatalogTemplates([]);
      setSelectedTemplateId(null);
    } finally {
      setLoadingTemplates(false);
    }
  }, [effectiveDocumentType, isOpen, readStoredTemplatePreference]);

  const loadPreview = useCallback(async (templateIdOverride?: number | null) => {
    if (isDynamicReportMode && loadingReportContext) {
      return;
    }

    if (reportContextError) {
      setPreviewLabel(selectedReportOption?.label || 'Finance Report');
      setHtmlContent(`<html><body style="font-family:sans-serif;padding:40px;text-align:center;color:#666"><h2>Preview Unavailable</h2><p>${reportContextError}</p></body></html>`);
      setLoading(false);
      return;
    }

    if (isDynamicReportMode && !resolvedReportContext) {
      return;
    }

    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      const effectiveTemplateId = templateIdOverride !== undefined ? templateIdOverride : selectedTemplateId;
      const templateParam = effectiveTemplateId ? `&templateId=${effectiveTemplateId}` : '';
      
      // Use template-preview API for preview
      const res = await fetch(
        getApiUrl(`/api/v1/reports/template-preview?documentType=${effectiveDocumentType}${templateParam}`),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            template: {
              document_type: effectiveDocumentType,
              id: effectiveTemplateId || undefined,
            },
            reportData: effectiveReportData,
          }),
        },
      );
      if (!res.ok) throw new Error(await readErrorMessage(res, 'Failed to load preview'));
      const json = await res.json();
      if (json.success) {
        setHtmlContent(json.data.html);
        setPreviewLabel(json.data.label || effectiveDocumentType);
      } else {
        throw new Error(json.error || 'Preview load failed');
      }
    } catch (err: unknown) {
      console.error('[FinanceReportPreviewModal] load error:', err);
      setHtmlContent(`<html><body style="font-family:sans-serif;padding:40px;text-align:center;color:#666">
        <h2>Preview Unavailable</h2><p>${getErrorMessage(err, 'Could not generate preview')}</p></body></html>`);
    } finally {
      setLoading(false);
    }
  }, [effectiveDocumentType, effectiveReportData, isDynamicReportMode, loadingReportContext, reportContextError, resolvedReportContext, selectedReportOption?.label, selectedTemplateId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(templatePreferenceKey, selectedTemplateId ? String(selectedTemplateId) : 'default');
  }, [selectedTemplateId, templatePreferenceKey]);

  useEffect(() => {
    if (isOpen) {
      loadTemplates(undefined, effectiveDocumentType);
    }
  }, [effectiveDocumentType, isOpen, loadTemplates]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void loadPreview();
  }, [isOpen, loadPreview]);

  useEffect(() => {
    if (isOpen) {
      return;
    }

    setLoading(false);
    setLoadingReportContext(false);
    setReportContextError(null);
    setHtmlContent(null);
    setPreviewLabel('');
  }, [isOpen]);

  // ── Print ─────────────────────────────────────────────────

  const handlePrint = async () => {
    let printWindow: Window | null = null;

    try {
      printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`<!doctype html><html><head><title>Preparing print…</title></head><body style="font-family: Inter, Arial, sans-serif; padding: 24px; color: #475569;">Preparing printable PDF…</body></html>`);
        printWindow.document.close();
      }

      setPrinting(true);

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const templateParam = selectedTemplateId ? `&templateId=${selectedTemplateId}` : '';
      const res = await fetch(
        getApiUrl(`/api/v1/reports/generate?documentType=${effectiveDocumentType}${templateParam}`),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            documentType: effectiveDocumentType,
            templateId: selectedTemplateId || undefined,
            reportData: effectiveReportData,
            period: effectivePeriod,
          }),
        },
      );

      if (!res.ok) {
        throw new Error(await readErrorMessage(res, 'Failed to prepare printable PDF'));
      }

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      if (printWindow) {
        printWindow.location.href = blobUrl;
        window.setTimeout(() => {
          try {
            printWindow?.focus();
            printWindow?.print();
          } catch (error) {
            console.error('[FinanceReportPreviewModal] Failed to trigger PDF print dialog:', error);
          }
        }, 1200);
        return;
      }

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      iframe.onload = () => {
        window.setTimeout(() => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          } finally {
            URL.revokeObjectURL(blobUrl);
            iframe.remove();
          }
        }, 1200);
      };
      iframe.src = blobUrl;
      document.body.appendChild(iframe);
    } catch (error: unknown) {
      if (printWindow && !printWindow.closed) {
        printWindow.close();
      }
      console.error('[FinanceReportPreviewModal] print error:', error);
      toast.error(getErrorMessage(error, 'Print failed'));
    } finally {
      setPrinting(false);
    }
  };

  // ── Download PDF ──────────────────────────────────────────

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const token = authService.getTokens()?.access_token;
      const templateParam = selectedTemplateId ? `&templateId=${selectedTemplateId}` : '';

      if (isCapacitor()) {
        // Mobile: get base64 and share
        const res = await fetch(
          getApiUrl(`/api/v1/reports/generate?documentType=${effectiveDocumentType}${templateParam}&format=base64`),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              documentType: effectiveDocumentType,
              templateId: selectedTemplateId || undefined,
              reportData: effectiveReportData,
              period: effectivePeriod,
            }),
          },
        );
        if (!res.ok) throw new Error(await readErrorMessage(res, 'PDF generation failed'));
        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'PDF generation failed');
        const { base64, filename } = json.data;
        const binaryStr = atob(base64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
        const file = new File([bytes], filename, { type: 'application/pdf' });
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: filename });
        } else {
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 10000);
        }
      } else {
        // Desktop: direct download
        const res = await fetch(
          getApiUrl(`/api/v1/reports/generate?documentType=${effectiveDocumentType}${templateParam}&format=download`),
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              documentType: effectiveDocumentType,
              templateId: selectedTemplateId || undefined,
              reportData: effectiveReportData,
              period: effectivePeriod,
            }),
          },
        );
        if (!res.ok) throw new Error(await readErrorMessage(res, 'PDF generation failed'));
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const disposition = res.headers.get('Content-Disposition') || '';
        const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
        a.download = filenameMatch?.[1] || `${effectiveDocumentType}_report.pdf`;
        a.href = url;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
      toast.success('PDF downloaded');
    } catch (error: unknown) {
      console.error('[FinanceReportPreviewModal] download error:', error);
      toast.error(getErrorMessage(error, 'PDF download failed'));
    } finally {
      setDownloading(false);
    }
  };

  // ── Email ─────────────────────────────────────────────────

  const handleEmailSend = async () => {
    const recipients = extractEmailAddresses(emailTo);
    if (recipients.length === 0) {
      toast.error('Please enter at least one recipient email address');
      return;
    }
    setEmailSending(true);
    try {
      const token = authService.getTokens()?.access_token;
      const res = await fetch('/api/v1/report-emails', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'sendReportEmail',
          reportType: effectiveDocumentType,
          reportData: {
            ...(effectiveReportData || {}),
            ...(effectivePeriod ? { period: effectivePeriod } : {}),
          },
          recipientEmail: recipients,
          cc: extractEmailAddresses(emailCc),
          bcc: extractEmailAddresses(emailBcc),
          subject: emailSubject.trim() || undefined,
          message: emailMessage || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Email failed');
      }
      if (data.success) {
        toast.success(`Queued for ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`);
        setEmailOpen(false);
        setEmailTo('');
        setEmailCc('');
        setEmailBcc('');
        setEmailSubject('');
        setEmailMessage('');
        setShowCcBcc(false);
      } else {
        throw new Error(data.error || 'Email failed');
      }
    } catch (err: unknown) {
      console.error('[FinanceReportPreviewModal] email error:', err);
      toast.error(getErrorMessage(err, 'Email sending failed'));
    } finally {
      setEmailSending(false);
    }
  };

  useEffect(() => {
    if (!emailOpen) return;
    const docLabel = DOCUMENT_TYPE_LABELS[effectiveDocumentType] || previewLabel || effectiveDocumentType;
    setEmailSubject(`${docLabel}${effectivePeriod?.from && effectivePeriod?.to ? ` (${effectivePeriod.from} to ${effectivePeriod.to})` : ''}`);
  }, [effectiveDocumentType, effectivePeriod, emailOpen, previewLabel]);

  const openTemplateDesigner = async () => {
    if (!canManageTemplates) {
      toast.error('Only owners and root users can customize report templates');
      return;
    }

    setOpeningDesigner(true);
    try {
      let nextTemplateId = selectedTemplateId;

      if (!nextTemplateId && availableTemplates.length > 0) {
        nextTemplateId = availableTemplates.find((template) => template.is_default)?.id ?? availableTemplates[0]?.id ?? null;
      }

      if (!nextTemplateId && catalogTemplates.length > 0) {
        const sourceCatalog = catalogTemplates.find((template) => template.document_type === effectiveDocumentType) ?? catalogTemplates[0];
        const token = authService.getTokens()?.access_token;
        const res = await fetch('/api/v1/reports/templates', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'enableCatalog', catalogId: sourceCatalog.id }),
        });

        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.error || 'Failed to enable template');
        }

        nextTemplateId = json.data?.id ?? null;
        if (nextTemplateId) {
          setSelectedTemplateId(nextTemplateId);
          await loadTemplates(nextTemplateId, effectiveDocumentType);
        }
      }

      setDesignerTemplateId(nextTemplateId ?? undefined);
      setTemplateDesignerOpen(true);
    } catch (err: unknown) {
      console.error('[FinanceReportPreviewModal] designer open error:', err);
      toast.error(getErrorMessage(err, 'Unable to open template designer'));
    } finally {
      setOpeningDesigner(false);
    }
  };

  if (!isOpen) return null;

  // UnifiedDesigner renders its own full-screen portal (default z-[130]) —
  // overlayClassName lifts it above this modal; do NOT wrap it in an opaque
  // backdrop or the backdrop covers the designer.
  const templateDesignerOverlay = templateDesignerOpen && canManageTemplates
    ? createPortal(
        <UnifiedDesigner
          type="report-template"
          documentId={designerTemplateId}
          overlayClassName="z-[140]"
          createOptions={{ subtype: effectiveDocumentType }}
          onClose={() => setTemplateDesignerOpen(false)}
            onSaved={async (doc) => {
              const savedTemplate = doc.payload as UnifiedTemplate;
              const nextId = savedTemplate.id ? Number(savedTemplate.id) : null;
              if (nextId) {
                setSelectedTemplateId(nextId);
                setDesignerTemplateId(nextId);
              }
              await loadTemplates(nextId, effectiveDocumentType);
              await loadPreview(nextId);
            }}
          />,
        document.body,
      )
    : null;

  const modalContent = (
    <div
      className={`fixed inset-0 isolate bg-black/60 backdrop-blur-sm flex z-[120] ${isCapacitorApp ? 'items-stretch justify-stretch p-0' : 'items-center justify-center p-4'}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`relative bg-white dark:bg-gray-900 shadow-2xl w-full overflow-hidden flex flex-col ${isCapacitorApp ? 'max-w-full h-[100dvh] max-h-[100dvh] rounded-none' : 'max-w-6xl h-[96vh] max-h-[96vh] rounded-2xl'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ───────────────────────────────────────── */}
        <div
          className="flex flex-wrap items-start justify-between gap-3 px-4 py-4 sm:px-6 flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, #1e3a5f 0%, #0d2137 100%)',
            paddingTop: isCapacitorApp ? `calc(${safeAreaTop} + 1rem)` : undefined,
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Printer className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {previewLabel ? `${previewLabel} Preview` : 'Finance Report Preview'}
              </h2>
              <p className="text-white/60 text-sm flex items-center gap-2 flex-wrap">
                {loadingTemplates ? (
                  <span className="inline-flex items-center gap-1 text-white/50 text-xs">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Loading templates…
                  </span>
                ) : (
                  <span className="text-white/50 text-xs">
                    Template: {availableTemplates.find((template) => template.id === selectedTemplateId)?.name || effectiveDocumentType}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 self-stretch sm:self-auto">
            <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-white/10 rounded-xl">
              {reportOptions && reportOptions.length > 1 && (
                <>
                  <span className="text-[11px] uppercase tracking-wide text-white/60">Report</span>
                  <select
                    value={selectedReportOptionId ?? ''}
                    onChange={(e) => setSelectedReportOptionId(e.target.value || null)}
                    className="bg-transparent text-white text-sm outline-none min-w-[180px]"
                    disabled={loadingReportContext}
                  >
                    {reportOptions.map((option) => (
                      <option key={option.id} value={option.id} className="text-gray-900">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <span className="text-[11px] uppercase tracking-wide text-white/60">Template</span>
              <select
                value={selectedTemplateId ?? ''}
                onChange={(e) => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)}
                className="bg-transparent text-white text-sm outline-none min-w-[180px]"
                disabled={loadingTemplates || loadingReportContext}
              >
                <option value="" className="text-gray-900">Default</option>
                {availableTemplates.map((template) => (
                  <option key={template.id} value={template.id} className="text-gray-900">
                    {template.name}{template.is_default ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
            {canManageTemplates && (
              <button
                onClick={openTemplateDesigner}
                disabled={openingDesigner || loadingReportContext}
                className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50 text-sm"
                title="Edit the report template used for this preview"
              >
                {openingDesigner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
                Customize
              </button>
            )}
            <button
              onClick={handlePrint}
              disabled={loading || loadingReportContext || printing}
              className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50 text-sm"
            >
              {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              {printing ? 'Preparing…' : 'Print'}
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={downloading || loading || loadingReportContext}
              className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50 text-sm"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download
            </button>
            <button
              onClick={() => setEmailOpen(!emailOpen)}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center gap-2 transition-colors text-sm"
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
            <button onClick={onClose} className="p-2.5 hover:bg-white/10 rounded-xl transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* ── Inline email form ────────────────────────────── */}
        {emailOpen && (
          <div className="px-4 py-3 sm:px-6 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-blue-600" />
                Email {previewLabel || 'Report'}
              </h4>
              {!showCcBcc && (
                <button
                  type="button"
                  onClick={() => setShowCcBcc(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Cc / Bcc
                </button>
              )}
            </div>

            <div className="relative">
              <div className="flex items-start gap-2">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-2 w-8 shrink-0">To</label>
                <div className="flex-1">
                  <EmailRecipientsInput
                    value={emailTo}
                    onChange={setEmailTo}
                    onFocus={() => setActiveEmailField('to')}
                    suggestions={emailSuggestions}
                    showSuggestions={showEmailSuggestions && activeEmailField === 'to'}
                    selectedSuggestionIndex={selectedEmailSuggestionIndex}
                    onLoadSuggestions={loadEmailSuggestions}
                    onSelectSuggestion={selectEmailSuggestion}
                    onKeyDown={handleEmailFieldKeyDown}
                    inputRef={emailToInputRef}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setContactPickerOpen(true)}
                  className={cn(
                    'shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    emailTo.trim()
                      ? 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600'
                      : 'bg-blue-600 text-white hover:bg-blue-700',
                  )}
                >
                  <Users className="w-3.5 h-3.5" />
                  {emailTo.trim() ? 'CRM list' : 'Pick from CRM'}
                </button>
              </div>
              {!emailTo.trim() && (
                <p className="mt-1 ml-10 text-[11px] text-blue-600 dark:text-blue-400">
                  No recipient yet? Open the CRM list and add one in a click.
                </p>
              )}

              {showCcBcc && (
                <>
                  <div className="flex items-start gap-2 mt-1.5">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-2 w-8 shrink-0">Cc</label>
                    <div className="flex-1">
                      <EmailRecipientsInput
                        value={emailCc}
                        onChange={setEmailCc}
                        onFocus={() => setActiveEmailField('cc')}
                        suggestions={emailSuggestions}
                        showSuggestions={showEmailSuggestions && activeEmailField === 'cc'}
                        selectedSuggestionIndex={selectedEmailSuggestionIndex}
                        onLoadSuggestions={loadEmailSuggestions}
                        onSelectSuggestion={selectEmailSuggestion}
                        onKeyDown={handleEmailFieldKeyDown}
                        inputRef={emailCcInputRef}
                      />
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mt-1.5">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-2 w-8 shrink-0">Bcc</label>
                    <div className="flex-1">
                      <EmailRecipientsInput
                        value={emailBcc}
                        onChange={setEmailBcc}
                        onFocus={() => setActiveEmailField('bcc')}
                        suggestions={emailSuggestions}
                        showSuggestions={showEmailSuggestions && activeEmailField === 'bcc'}
                        selectedSuggestionIndex={selectedEmailSuggestionIndex}
                        onLoadSuggestions={loadEmailSuggestions}
                        onSelectSuggestion={selectEmailSuggestion}
                        onKeyDown={handleEmailFieldKeyDown}
                        inputRef={emailBccInputRef}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-start gap-2">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 pt-2 w-8 shrink-0">Subj</label>
              <input
                type="text"
                placeholder="Subject line"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <textarea
              placeholder="Optional message"
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 outline-none resize-none"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-400">
                {extractEmailAddresses(emailTo).length > 0
                  ? `${extractEmailAddresses(emailTo).length} recipient${extractEmailAddresses(emailTo).length === 1 ? '' : 's'}${extractEmailAddresses(emailCc).length > 0 ? ` + ${extractEmailAddresses(emailCc).length} Cc` : ''}${extractEmailAddresses(emailBcc).length > 0 ? ` + ${extractEmailAddresses(emailBcc).length} Bcc` : ''}`
                  : 'No recipients'}
              </span>
              <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setEmailOpen(false);
                  setEmailCc('');
                  setEmailBcc('');
                  setEmailMessage('');
                  setEmailSubject('');
                  setShowCcBcc(false);
                }}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEmailSend}
                disabled={emailSending || !emailTo.trim()}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {emailSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send
              </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Preview Content ───────────────────────────────── */}
        <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-900">
          {loading || loadingReportContext ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
                <p className="text-slate-500 text-sm">Generating preview…</p>
              </div>
            </div>
          ) : htmlContent ? (
            <iframe
              ref={iframeRef}
              srcDoc={htmlContent}
              className="w-full h-full border-0"
              title="Report Preview"
              sandbox="allow-same-origin"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-slate-500 text-sm">Preview unavailable</p>
            </div>
          )}
        </div>
      </div>

      <EmailContactPickerModal
        isOpen={contactPickerOpen}
        onClose={() => setContactPickerOpen(false)}
        onAddRecipients={(recipients) => appendRecipientsToField(recipients, 'to')}
        title={`Add ${previewLabel || 'report'} recipients`}
        defaultTab="crm"
      />
    </div>
  );

  return (
    <>
      {templateDesignerOverlay}
      {modalContent}
    </>
  );
}
